import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EmployeeBenefitType } from 'apps/user/types/on-boarding.type';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import {
  AssignBeneficiaryDto,
  Benefit
} from 'service/dto/employee-benefit.dto';
import {
  AssignTrusteeDto,
  BeneficiariesAllocationDto
} from 'service/dto/general.dto';

export const useCreateEmployeeBenefit = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (employeeBenefitType: EmployeeBenefitType): Promise<void> => {
      return services.employeeService.createEmployeeBenefit(
        employeeBenefitType
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-benefit']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetEmployeeBenefitList = () =>
  useQuery(['benefits'], () => services.employeeService.getEmployeeBenefits(), {
    refetchOnWindowFocus: false
  });

export const useDeleteEmployeeBenefit = () =>
  useMutation(
    (benefitId: number) =>
      services.employeeService.deleteEmployeeBenefit(benefitId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useAssignEmployeeBenefitBeneficiary = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (assignBeneficiaryDto: AssignBeneficiaryDto): Promise<void> => {
      return services.employeeService.assignBeneficiary(assignBeneficiaryDto);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-benefit-beneficiary']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetEmployeeBenefitDetails = ({
  benefitId
}: {
  benefitId: number;
}) => {
  return useQuery<Benefit, AxiosError<Error>>(
    ['get_benefit', benefitId],
    () => services.employeeService.getEmployeeBenefit(benefitId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!benefitId
    }
  );
};

export const useUnAssignEmployeeBenefitBeneficiary = () =>
  useMutation(
    ({ allocationId }: { allocationId?: number }) =>
      services.employeeService.unAssignEmployeeBenefitBeneficiary(allocationId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useEmployeeBenefitAllocationBulkEdit = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (beneficiariesAllocationDto: BeneficiariesAllocationDto): Promise<void> => {
      return services.employeeService.editBulkEmployeeBenefitBeneficiary(
        beneficiariesAllocationDto
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['benefit-allocation-edit']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useRemoveBenefitTrustee = () =>
  useMutation(
    ({ assetId, trusteeId }: { assetId: number; trusteeId: number }) =>
      services.employeeService.removeBenefitTrustee(assetId, trusteeId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useAssignBenefitTrustee = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ assetId, trusteeId, note }: AssignTrusteeDto): Promise<void> => {
      return services.employeeService.assignBenefitTrustee(
        assetId,
        trusteeId,
        note
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-benefit-trustee']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};
